<script setup lang="ts">
import { inject } from "vue"
import { storeToRefs } from "pinia"
import Button from "@/components/ui/button/Button.vue"
import Modal from "@/components/af/Modal/Modal.vue"
import InfoCard from "@/components/af/InfoCard/InfoCard.vue"

import { useModalStore } from "@/components/af/Modal/Modal.store"
import { useI18nStore } from "@/stores/i18n"
import { useUserStore } from "@/stores/user"
import { useHeaderControlsStore } from "@/components/af/HeaderControls/HeaderControls.store"

import { useHelpStore } from "@/components/af/Help/help.store"

import type { Translate } from "@/plugins/i18n/types"
import { useCookies } from "@vueuse/integrations/useCookies"

const t = inject("t") as Translate

const modalStore = useModalStore()
const { trackModal } = modalStore
const headerControlsStore = useHeaderControlsStore()
const { isModalOpen, isModalSubmitDisabled } = storeToRefs(headerControlsStore)

const { assessmentSlug } = storeToRefs(useHelpStore())

const i18nStore = useI18nStore()
const { currentLocale, currentLang } = storeToRefs(i18nStore)

const userStore = useUserStore()

type Emits = {
  onNotPassed: [string]
}
const emit = defineEmits<Emits>()

const afAuthSession = useCookies().get("af-session-auth")
const sharepool = afAuthSession?.sharepool

const modalProps = {
  iconName: "fa-thin fa-universal-access",
  title: t("assessment.accessibility.skip_assessment.modal.title"),
  body: sharepool
    ? t("assessment.accessibility.skip_assessment.modal.body")
    : t("assessment.accessibility.skip_assessment.modal.body_alt"),
  cancelBtnText: t("cancel"),
  submitBtnText: sharepool
    ? t("assessment.accessibility.skip_assessment.modal.cta")
    : t("assessment.accessibility.skip_assessment.modal.cta_alt"),
}
const handleModal = () => {
  isModalOpen.value = true
  const trackParams = {
    language: currentLang.value,
    name: "accessibility",
    user_email: userStore.getUserEmail,
    user_uuid: userStore.getUserUuid,
  }
  trackModal(trackParams)
}
</script>

<template>
  <Button
    variant="tertiary"
    class="group border-grey-lighten-3! text-grey h-11 w-auto gap-2 rounded-full px-4 py-3"
    :class="currentLocale.iso_code_639 === 'ar-SA' ? 'space-x-reverse' : ''"
    :aria-label="$t('assessment.accessibility.skip_assessment.cta')"
    @click="handleModal"
  >
    <FaIcon
      icon="fas fa-universal-access"
      aria-hidden="true"
      class="h-[21px] w-[21px]"
    />
    <span class="text-base font-light">
      {{ $t("assessment.accessibility.skip_assessment.cta") }}
    </span>

    <span
      class="bg-grey absolute z-50 hidden rounded p-3 px-3 py-2 text-center text-xs text-white group-hover:block group-focus:block"
      :class="currentLocale.iso_code_639 === 'ar-SA' ? 'left-14' : 'right-14'"
    >
      {{ $t("assessment.accessibility.skip_assessment.cta") }}
    </span>
  </Button>
  <Modal
    v-bind="modalProps"
    v-model:isModalOpen="isModalOpen"
    v-model:isModalSubmitDisabled="isModalSubmitDisabled"
    @onSubmit="emit('onNotPassed', assessmentSlug)"
    @onCancel="isModalOpen = false"
  >
    <template #bottom>
      <p class="text-afbranding-text mb-4 text-center font-light">
        {{
          t("assessment.accessibility.skip_assessment.modal.restart_cooldown", {
            hour: 24,
          })
        }}
      </p>
      <InfoCard
        :body="$t('assessment.accessibility.skip_assessment.modal.warning')"
      />
    </template>
  </Modal>
</template>
